import * as Yup from 'yup';

import { AddressSchema } from './address';
import { OrderSettingsSchema } from './order-settings';

export const VendorOrderingSchema = Yup.object().shape({
  send_methods: Yup.array().of(Yup.string().required()).required(),
  default_send_method: Yup.string().required(),
  download_file_format: Yup.string().defined(),
});

export const VendorReceivingSchema = Yup.object().shape({
  invoice_formats: Yup.array().of(Yup.string().required()).required(),
});

export const VendorPricingSchema = Yup.object().shape({
  sources: Yup.array().of(Yup.string().required()).required(),
  has_volume_pricing: Yup.boolean().required(),
});

export const VendorIntegrationSchema = Yup.object().shape({
  normalized_name: Yup.string().required(),
  label: Yup.string().required(),
  state: Yup.string().required(),
  ordering: VendorOrderingSchema.required(),
  receiving: VendorReceivingSchema.optional().nullable().default(null),
  pricing: VendorPricingSchema.optional().nullable().default(null),
});

export type VendorIntegration = Yup.InferType<typeof VendorIntegrationSchema>;

import type { Product } from './product';

// form validation rules
export const VendorSchema = Yup.object().shape({
  vendor_id: Yup.string().required(),
  store_id: Yup.string().required(),
  name: Yup.string().required(),
  normalized_name: Yup.string().defined(),
  account_number: Yup.string().defined(),
  terms: Yup.string().defined(),
  notes: Yup.string().defined(),
  invoice_formats: Yup.array().of(Yup.string().required()).required(),
  accepts_ai_csv: Yup.boolean().required(),
  order_settings: OrderSettingsSchema.required(),
  address: AddressSchema,
  disabled: Yup.boolean().required(),

  updated_at: Yup.date().required(),
});

export type Vendor = Yup.InferType<typeof VendorSchema>;

// A vendor with no native invoice parser that instead goes through the AI-CSV
// receiving flow. accepts_ai_csv with a non-empty invoice_formats is
// intentionally not generic — native parsers win.
export const isGenericAiVendor = (vendor: Vendor): boolean =>
  vendor.accepts_ai_csv && vendor.invoice_formats.length === 0;

export const yearlyPurchases = (vendor: Vendor, products: Product[]): number => {
  return products
    .filter((product) => product.vendor_id === vendor.vendor_id)
    .reduce((acc, product) => acc + product.stats.last_365 * product.last_cost, 0);
};

export const lastReceivedDate = (vendor: Vendor, products: Product[]): Date => {
  return products
    .filter((product) => product.vendor_id === vendor.vendor_id && product.last_received)
    .reduce((acc, product) => {
      if (!product.last_received) {
        return acc;
      }

      return product.last_received.getTime() > acc.getTime() ? product.last_received : acc;
    }, new Date(0));
};
